import React, { createContext, useState, useEffect, useCallback } from 'react';
import { User, AuthState, AuthContextType, RegisterUserData } from '../types';
import { authService } from '../services/authService';

// Create the context with a default value
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component that wraps your app and makes auth available
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // Get current token
  const [token, setToken] = useState<string | null>(authService.getToken());

  // Update token when it changes in localStorage
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token') {
        setToken(event.newValue);
      } else if (event.key === 'user') {
        // If user data changes in localStorage, update state
        try {
          const userData = event.newValue ? JSON.parse(event.newValue) : null;
          if (userData) {
            setState(prev => ({
              ...prev,
              user: userData,
              isAuthenticated: true,
              error: null
            }));
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };

    // Also add a direct storage listener for the current window
    const checkLocalStorage = () => {
      const storedToken = authService.getToken();
      if (storedToken !== token) {
        setToken(storedToken);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check localStorage every second for same-window updates
    const interval = setInterval(checkLocalStorage, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [token]);

  // Load user from token on mount or when token changes
  useEffect(() => {
    const loadUser = async () => {
      setState(prev => ({ ...prev, isLoading: true }));
      try {
        if (authService.isAuthenticated()) {
          const user = await authService.getCurrentUser();
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
        } else {
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('Error loading user:', error);
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Failed to load user'
        });
      }
    };

    loadUser();
  }, [token]); // Re-run when token changes

  // Login function
  const login = async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      // Try to login and get token
      const newToken = await authService.login(email, password);
      setToken(newToken);

      // Get user data - from either localStorage or decoded token
      const user = await authService.getCurrentUser();

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      return user; // Return user data
    } catch (error) {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Invalid credentials'
      });
      throw error; // Re-throw to let the component handle it
    }
  };

  // Register function
  const register = async (data: RegisterUserData) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const newToken = await authService.register(data);
      setToken(newToken);

      // Get user data - from either localStorage or decoded token
      const user = await authService.getCurrentUser();

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      return user; // Return user data
    } catch (error) {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Registration failed'
      });
      throw error; // Re-throw to let the component handle it
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setToken(null); // Clear token state
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
  };

  // Clear error
  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  // Context value
  const value = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    token,
    login,
    register,
    logout,
    clearError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook for components to get the auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};